/**
 * OnboardingReviewPage — "Demandes d'accès": the LAB ONBOARDING REVIEW console. It surfaces the full
 * intake → review → approve funnel the public self-signup feeds:
 *
 *   1. INTAKE   — a prospect submits a LabAccessRequest from the public portal (statut `demande`).
 *   2. REVIEW   — this inbox lists every request (filterable by statut/wilaya/substring); the operator opens
 *                 a request in the <ReviewDrawer> (dossier + email-template preview) and takes it into triage.
 *   3. APPROVE  — from the drawer, Approuver opens the provision form → creates the Laboratoire + owner and
 *                 links the request; Rejeter records a reason. Each transition queues the prospect email.
 *
 * TWO TABS: the request INBOX (the new flow) + the retained lab DIRECTORY/provision (the existing
 * <OnboardingPage>). The console is super-admin-only (the route rides <PlatformAdminGuard>); this screen ALSO
 * renders <ForbiddenState> defensively and short-circuits offline + the cloud-only "not mounted here" state.
 * Full 5-state DoD on the inbox read. Token-driven, AA-contrast, RTL-safe, FR/AR i18n.
 *
 * Registers its own i18n at module-load (registerOnboardingRequestsMessages) — no shell hookup needed.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Pagination,
  Select,
  Tabs,
  Table,
  type Column,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent } from "../api";
import { useIsPlatformAdmin } from "../session";
import { formatDate, demandeStatutTone } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { DistributionBar, StatChips, type DistributionSlice } from "../components/Distribution";
import { ReviewDrawer } from "../components/ReviewDrawer";
import { OnboardingPage } from "./OnboardingPage";
import { useLabAccessRequests } from "./onboardingRequests.api";
import {
  DEMANDE_STATUTS,
  type DemandeStatut,
  type LabAccessRequestFilters,
  type LabAccessRequestRow,
} from "./onboardingRequests.types";
import { registerOnboardingRequestsMessages } from "./onboardingRequests.messages";

registerOnboardingRequestsMessages();

const PAGE_SIZE = 20;

/** A stable tone per funnel step for the insight meter (paired with a label — never color-only). */
const STATUT_TONE: Record<DemandeStatut, DistributionSlice["tone"]> = {
  demande: "info",
  en_revue: "warn",
  approuvee: "success",
  rejetee: "danger",
};

/** The INBOX tab — the request triage directory + the review drawer (the intake→review→approve flow). */
function RequestInbox() {
  const { t, locale } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<DemandeStatut | "">("");
  const [wilaya, setWilaya] = useState("");
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<LabAccessRequestRow | null>(null);

  const filters: LabAccessRequestFilters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(statut ? { statut } : {}),
      ...(wilaya.trim() ? { wilaya: wilaya.trim() } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [search, statut, wilaya, page],
  );

  const query = useLabAccessRequests(filters, isAdmin && online);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  const totalPages = query.data?.totalPages ?? 1;
  const total = query.data?.total ?? 0;
  const hasFilters = !!(search.trim() || statut || wilaya.trim());

  // Per-page funnel insight (DERIVED from the loaded rows — schema-free, no extra read).
  const flowSlices = useMemo<DistributionSlice[]>(() => {
    const counts = new Map<DemandeStatut, number>();
    for (const r of rows) counts.set(r.statut, (counts.get(r.statut) ?? 0) + 1);
    return DEMANDE_STATUTS.filter((s) => counts.has(s)).map((s) => ({
      key: s,
      label: t(`mgmt.reqs.statut.${s}`),
      value: counts.get(s) ?? 0,
      tone: STATUT_TONE[s],
    }));
  }, [rows, t]);
  const pendingCount = useMemo(
    () => rows.filter((r) => r.statut === "demande" || r.statut === "en_revue").length,
    [rows],
  );

  const columns: ReadonlyArray<Column<LabAccessRequestRow>> = [
    {
      key: "reference",
      header: t("mgmt.reqs.col.reference"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.reference.toLowerCase(),
      cell: (r) => <span className="nv-ltr-island">{r.reference}</span>,
    },
    {
      key: "lab",
      header: t("mgmt.reqs.col.lab"),
      sortable: true,
      sortAccessor: (r) => r.nomLabo.toLowerCase(),
      cell: (r) => r.nomLabo,
    },
    {
      key: "contact",
      header: t("mgmt.reqs.col.contact"),
      cell: (r) => <span className="nv-ltr-island">{r.email}</span>,
    },
    {
      key: "wilaya",
      header: t("mgmt.reqs.col.wilaya"),
      sortable: true,
      sortAccessor: (r) => r.wilaya.toLowerCase(),
      cell: (r) => r.wilaya || "—",
    },
    {
      key: "statut",
      header: t("mgmt.reqs.col.statut"),
      sortable: true,
      sortAccessor: (r) => r.statut,
      cell: (r) => (
        <Badge tone={demandeStatutTone(r.statut)} glyph="●">
          {t(`mgmt.reqs.statut.${r.statut}`)}
        </Badge>
      ),
    },
    {
      key: "received",
      header: t("mgmt.reqs.col.received"),
      sortable: true,
      sortAccessor: (r) => r.createdAt ?? "",
      cell: (r) => <span className="nv-ltr-island">{formatDate(r.createdAt, locale)}</span>,
    },
    {
      key: "actions",
      header: t("mgmt.reqs.col.actions"),
      align: "end",
      cell: (r) => (
        <Button size="sm" variant="secondary" onClick={() => setActive(r)}>
          {t("mgmt.reqs.action.review")}
        </Button>
      ),
    },
  ];

  if (!online) {
    return <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />;
  }

  // Cloud-only module on a local box → its routes 404. Surface the clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Card title={t("mgmt.unavailable.title")}>
        <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.unavailable.desc")}</p>
      </Card>
    );
  }

  const onFilterChange =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setPage(1);
    };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      {!query.isLoading && !query.isError && rows.length > 0 && (
        <Card title={t("mgmt.reqs.flow.title")}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <StatChips
              chips={[
                { key: "total", label: t("mgmt.reqs.flow.title"), value: rows.length },
                {
                  key: "pending",
                  label: t("mgmt.reqs.flow.pending"),
                  value: pendingCount,
                  tone: pendingCount > 0 ? "warn" : "neutral",
                },
              ]}
            />
            <DistributionBar
              slices={flowSlices}
              emptyLabel={t("mgmt.reqs.flow.empty")}
              ariaLabel={t("mgmt.reqs.flow.aria")}
            />
          </div>
        </Card>
      )}

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            gap: "var(--nv-s-3)",
            flexWrap: "wrap",
            padding: "var(--nv-s-3)",
            borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          }}
        >
          <div style={{ flex: "1 1 16rem", minWidth: "12rem" }}>
            <Input
              value={search}
              onChange={(e) => onFilterChange(setSearch)(e.target.value)}
              placeholder={t("mgmt.reqs.search")}
              aria-label={t("mgmt.reqs.search")}
            />
          </div>
          <Select
            value={statut}
            onChange={(e) => onFilterChange(setStatut)(e.target.value as DemandeStatut | "")}
            aria-label={t("mgmt.reqs.filter.statut")}
          >
            <option value="">{`${t("mgmt.reqs.filter.statut")} — ${t("mgmt.reqs.filter.all")}`}</option>
            {DEMANDE_STATUTS.map((s) => (
              <option key={s} value={s}>
                {t(`mgmt.reqs.statut.${s}`)}
              </option>
            ))}
          </Select>
          <div style={{ flex: "0 1 12rem", minWidth: "9rem" }}>
            <Input
              value={wilaya}
              onChange={(e) => onFilterChange(setWilaya)(e.target.value)}
              placeholder={t("mgmt.reqs.filter.wilaya")}
              aria-label={t("mgmt.reqs.filter.wilaya")}
              className="nv-ltr-island"
            />
          </div>
        </div>

        <div style={{ padding: "var(--nv-s-3)" }}>
          {query.isLoading ? (
            <LoadingState variant="table" rows={6} />
          ) : query.isError ? (
            <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
          ) : (
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              caption={t("mgmt.reqs.title")}
              emptyTitle={hasFilters ? t("mgmt.reqs.empty.filtered") : t("mgmt.reqs.empty.title")}
              {...(hasFilters ? {} : { emptyDescription: t("mgmt.reqs.empty.desc") })}
            />
          )}
        </div>

        {!query.isLoading && !query.isError && totalPages > 1 && (
          <div style={{ padding: "var(--nv-s-3)", borderTop: "1px solid var(--nv-border-subtle, var(--nv-line-faint))" }}>
            <Pagination page={page} pageCount={totalPages} total={total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <ReviewDrawer request={active} onClose={() => setActive(null)} />
    </div>
  );
}

/** The screen shell: header + a two-tab surface (the review inbox + the retained lab directory). */
export function OnboardingReviewPage() {
  const { t } = useT();
  const isAdmin = useIsPlatformAdmin();
  const [tab, setTab] = useState("inbox");

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  const tabs: ReadonlyArray<{ id: string; label: ReactNode; content: ReactNode }> = [
    { id: "inbox", label: t("mgmt.reqs.tab.inbox"), content: <RequestInbox /> },
    // The retained lab directory/provision — the existing screen embedded as the second tab.
    { id: "directory", label: t("mgmt.reqs.tab.directory"), content: <OnboardingPage /> },
  ];

  return (
    <Screen>
      <PageHeader title={t("mgmt.reqs.title")} subtitle={t("mgmt.reqs.subtitle")} />
      <Tabs items={tabs} value={tab} onChange={setTab} aria-label={t("mgmt.reqs.title")} />
    </Screen>
  );
}
